import { Link, NavLink } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { Menu, X, Sun, Moon, ChevronDown } from "lucide-react";
import { useState } from "react";

const YAMAHA_BASE = {
    es: "https://www.yamaha-motor.eu/es/es",
    en: "https://www.yamaha-motor.eu/gb/en",
};

export default function Header() {
    const { lang, toggle, t } = useLang();

    const PRODUCTS_SUBMENU = [
        { labelKey: "products_acc", href: `${YAMAHA_BASE[lang]}/accessories/` },
        { labelKey: "products_apparel", href: `${YAMAHA_BASE[lang]}/clothing-merchandise/clothing/` },
        { labelKey: "products_parts", href: `${YAMAHA_BASE[lang]}/service-support/parts-catalogue/` },
    ];
    const { theme, toggle: toggleTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const [openProducts, setOpenProducts] = useState(false);
    const [openProductsDesktop, setOpenProductsDesktop] = useState(false);

    const linksStart = [
        { to: "/motos", label: t.nav.new, testid: "nav-new-link" },
        { to: "/ocasion", label: t.nav.used, testid: "nav-used-link" },
    ];
    const linksEnd = [
        { to: "/servicio", label: t.nav.service, testid: "nav-service-link" },
        { to: "/contacto", label: t.nav.contact, testid: "nav-contact-link" },
    ];

    return (
        <header
            className="sticky top-0 z-50 w-full glass-strong border-b border-black/10"
            data-testid="site-header"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
                <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                    <span
                        className="inline-flex items-center justify-center rounded-full px-4 py-2 shadow-sm"
                        style={{ backgroundColor: "#ffffff" }}
                    >
                        <img
                            src="https://ls39motos.com/wp-content/uploads/2025/03/LS39-Motos-Logo-con-Yamaha-e1743071508796-2048x1790.png"
                            alt="LS39 Motos · Yamaha Mallorca"
                            className="h-8 w-auto sm:h-10"
                        />
                    </span>
                    <span className="sr-only">LS39 Motos</span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden items-center gap-7 lg:flex">
                    {linksStart.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `text-[11px] uppercase tracking-[0.18em] transition-colors hover:text-neutral-900 ${
                                    isActive ? "text-neutral-900" : "text-neutral-500"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}

                    {/* Productos dropdown — hover */}
                    <div
                        className="relative"
                        data-testid="nav-products-dropdown"
                        onMouseEnter={() => setOpenProductsDesktop(true)}
                        onMouseLeave={() => setOpenProductsDesktop(false)}
                    >
                        <button
                            className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-900 transition-colors"
                            data-testid="nav-products-btn"
                        >
                            {t.nav.products}
                            <ChevronDown
                                size={11}
                                className={`transition-transform duration-200 ${openProductsDesktop ? "rotate-180" : ""}`}
                            />
                        </button>
                        {openProductsDesktop && (
                            <div className="absolute left-0 top-full pt-2 z-50">
                                <div className="glass-strong border border-black/10 py-1 min-w-[210px]">
                                    {PRODUCTS_SUBMENU.map((item) => (
                                        <a
                                            key={item.href}
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => setOpenProductsDesktop(false)}
                                            className="block px-5 py-3 text-[10px] uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-900 hover:bg-black/5 transition-colors"
                                        >
                                            {t.nav[item.labelKey]}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {linksEnd.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `text-[11px] uppercase tracking-[0.18em] transition-colors hover:text-neutral-900 ${
                                    isActive ? "text-neutral-900" : "text-neutral-500"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={toggleTheme}
                        data-testid="theme-toggle-btn"
                        aria-label="toggle theme"
                        className="text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-neutral-900 border border-black/15 p-2"
                    >
                        {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
                    </button>
                    <button
                        onClick={toggle}
                        data-testid="lang-toggle-btn"
                        className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-neutral-900 border border-black/15 px-3 py-2"
                    >
                        {lang === "es" ? "EN" : "ES"}
                    </button>
                    <button
                        className="lg:hidden text-neutral-900"
                        onClick={() => setOpen((o) => !o)}
                        data-testid="mobile-menu-toggle"
                        aria-label="menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div className="lg:hidden border-t border-black/10 bg-white/90 backdrop-blur-xl">
                    <div className="flex flex-col px-6 py-4">
                        {linksStart.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`m-${l.testid}`}
                                className="py-3 text-sm uppercase tracking-[0.2em] text-neutral-600"
                            >
                                {l.label}
                            </NavLink>
                        ))}

                        {/* Productos mobile — toggle */}
                        <button
                            onClick={() => setOpenProducts((o) => !o)}
                            className="flex items-center justify-between py-3 text-sm uppercase tracking-[0.2em] text-neutral-600"
                            data-testid="m-nav-products-btn"
                        >
                            {t.nav.products}
                            <ChevronDown
                                size={14}
                                className={`transition-transform duration-200 ${openProducts ? "rotate-180" : ""}`}
                            />
                        </button>
                        {openProducts && (
                            <div className="pl-4 pb-2 flex flex-col gap-1">
                                {PRODUCTS_SUBMENU.map((item) => (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => {
                                            setOpen(false);
                                            setOpenProducts(false);
                                        }}
                                        className="py-2 text-xs uppercase tracking-[0.2em] text-neutral-500"
                                    >
                                        {t.nav[item.labelKey]}
                                    </a>
                                ))}
                            </div>
                        )}

                        {linksEnd.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`m-${l.testid}`}
                                className="py-3 text-sm uppercase tracking-[0.2em] text-neutral-600"
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
